import React, { createContext, useContext, useState, useEffect } from "react";
import i18next from "i18next";
import { t, changeLanguage, getUserLanguage, LOCALE_CACHE_KEY } from "./index";

// Define the context type
interface I18nContextType {
  i18n: typeof i18next;
  t: typeof t;
  changeLanguage: typeof changeLanguage;
  currentLanguage: string;
  isLoaded: boolean;
}

// Create context with default values
const I18nContext = createContext<I18nContextType>({
  i18n: i18next,
  t,
  changeLanguage,
  currentLanguage: i18next.language,
  isLoaded: false,
});

// Custom hook to use i18n
export const useI18n = () => useContext(I18nContext);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Always get the fresh language from localStorage on mount
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    getUserLanguage()
  );
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // Extended change language function that updates state
  const handleChangeLanguage = async (lang: string) => {
    const result = await changeLanguage(lang);

    // Get the actual language that was set (in case a fallback was used)
    const actualLang = i18next.language;

    setCurrentLanguage(actualLang);
    return result;
  };

  // Check on mount and when currentLanguage changes that our state and i18next are in sync
  useEffect(() => {
    if (i18next.language !== currentLanguage) {
      // If localStorage has a different value than our state, prioritize localStorage
      const storedLang = localStorage.getItem(LOCALE_CACHE_KEY);
      if (storedLang && storedLang !== currentLanguage) {
        setCurrentLanguage(storedLang);
        i18next.changeLanguage(storedLang);
      } else {
        i18next.changeLanguage(currentLanguage);
      }
    }
  }, [currentLanguage]);

  // Listen for language changes from i18next
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng);
      // Check if resources are loaded for the new language
      setIsLoaded(i18next.hasResourceBundle(lng, "translation"));
    };

    const handleLoaded = () => {
      setIsLoaded(true);
    };

    i18next.on("languageChanged", handleLanguageChanged);
    i18next.on("loaded", handleLoaded);

    // Check initial state
    if (
      i18next.language &&
      i18next.hasResourceBundle(i18next.language, "translation")
    ) {
      setIsLoaded(true);
    }

    return () => {
      i18next.off("languageChanged", handleLanguageChanged);
      i18next.off("loaded", handleLoaded);
    };
  }, []);

  const contextValue = {
    i18n: i18next,
    t,
    changeLanguage: handleChangeLanguage,
    currentLanguage,
    isLoaded,
  };

  return (
    <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
  );
};

export default I18nProvider;
